import React, { useEffect, useState } from "react";
import { AuditLog } from "../../types/admin";
import AdminNav from "../../components/AdminNav";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5173";

const AdminAuditLogs: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [actionFilter, setActionFilter] = useState("");
  const [actions, setActions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, actionFilter]);

  useEffect(() => {
    // アクションのユニークリストを生成
    if (logs.length > 0) {
      const uniqueActions = Array.from(new Set(logs.map((log) => log.action)));
      setActions(uniqueActions);
    }
  }, [logs]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("auth_token");
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(actionFilter && { action: actionFilter }),
      });

      const response = await fetch(
        `${API_BASE_URL}/api/admin/audit-logs?${params}`,
        {
          credentials: "include",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("監査ログの取得に失敗しました");
      }

      const data = await response.json();
      if (data.success) {
        setLogs(data.data.logs);
        setTotal(data.data.total);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "エラーが発生しました");
    } finally {
      setLoading(false);
    }
  };

  const getActionLabel = (action: string): string => {
    const labels: { [key: string]: string } = {
      view_analytics: "統計閲覧",
      view_user_list: "ユーザー一覧閲覧",
      view_user_details: "ユーザー詳細閲覧",
      update_user_role: "ユーザーロール変更",
      suspend_user: "ユーザーサスペンド",
      activate_user: "ユーザー有効化",
      view_contact_submissions: "お問い合わせ一覧閲覧",
      update_contact_submission: "お問い合わせ更新",
      view_company_templates: "企業テンプレート一覧閲覧",
      create_company_template: "企業テンプレート作成",
      update_company_template: "企業テンプレート更新",
      delete_company_template: "企業テンプレート削除",
    };
    return labels[action] || action;
  };

  const totalPages = Math.ceil(total / limit);

  if (loading && logs.length === 0) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <>
      <AdminNav />
      <div className="p-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">監査ログ</h1>
          <div className="text-sm text-gray-600">総件数: {total}</div>
        </div>

        {/* フィルター */}
        <div className="mb-6 flex gap-3 items-center">
          <label className="text-sm font-medium text-gray-700">
            アクション:
          </label>
          <select
            value={actionFilter}
            onChange={(e) => {
              setActionFilter(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">すべて</option>
            {actions.map((action) => (
              <option key={action} value={action}>
                {getActionLabel(action)}
              </option>
            ))}
          </select>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* ログテーブル */}
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  日時
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  管理者
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  アクション
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  対象タイプ
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  対象ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  IPアドレス
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {logs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {log.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(log.created_at + "Z").toLocaleString("ja-JP", {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                      timeZone: "Asia/Tokyo",
                    })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div>
                      <div className="font-medium">{log.admin_username}</div>
                      <div className="text-gray-500 text-xs">
                        {log.admin_email}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                      {getActionLabel(log.action)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {log.target_type || "-"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {log.target_id || "-"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {log.ip_address}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => setSelectedLog(log)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      詳細
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ページネーション */}
        <div className="mt-6 flex justify-between items-center">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-blue-700"
          >
            前へ
          </button>
          <span className="text-sm text-gray-600">
            {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-blue-700"
          >
            次へ
          </button>
        </div>

        {/* ログ詳細モーダル */}
        {selectedLog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold mb-4">ログ詳細</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    ログID
                  </label>
                  <p className="mt-1 text-sm text-gray-900">{selectedLog.id}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    日時
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {new Date(selectedLog.created_at + "Z").toLocaleString(
                      "ja-JP",
                      {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                        timeZone: "Asia/Tokyo",
                      }
                    )}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    管理者
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedLog.admin_username} ({selectedLog.admin_email})
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    アクション
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {getActionLabel(selectedLog.action)}
                  </p>
                </div>

                {selectedLog.target_type && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      対象タイプ
                    </label>
                    <p className="mt-1 text-sm text-gray-900">
                      {selectedLog.target_type}
                    </p>
                  </div>
                )}

                {selectedLog.target_id && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      対象ID
                    </label>
                    <p className="mt-1 text-sm text-gray-900">
                      {selectedLog.target_id}
                    </p>
                  </div>
                )}

                {selectedLog.details && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      詳細
                    </label>
                    <pre className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded border overflow-x-auto">
                      {JSON.stringify(JSON.parse(selectedLog.details), null, 2)}
                    </pre>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    IPアドレス
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedLog.ip_address}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    ユーザーエージェント
                  </label>
                  <p className="mt-1 text-sm text-gray-900 break-all">
                    {selectedLog.user_agent}
                  </p>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setSelectedLog(null)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
                >
                  閉じる
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AdminAuditLogs;
